import {useState, type FormEvent} from 'react';
import {ErrorNotice, Loading} from './components';
import type {hermesActions} from './lib/store';
import type {HermesState} from './lib/types';

type Props = {state: HermesState; actions: typeof hermesActions};
type UsernameDraft = {username: string; expectedUsername: string};

export function AccountProfileSettings({state, actions}: Props) {
  const [draft, setDraft] = useState<UsernameDraft | null>(null);
  const profile = state.accountProfile.data;
  const busy = state.operation.label !== null || state.accountProfile.status !== 'ready';
  const stale = draft !== null && profile !== null && draft.expectedUsername !== profile.username;
  const changed = draft !== null && draft.username !== draft.expectedUsername;

  async function saveUsername(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || stale || !changed || draft === null) return;
    try {
      await actions.saveAccountUsername(draft.username, draft.expectedUsername);
      setDraft(null);
    } catch { /* Preserve the draft. The shared operation notice displays the full request error. */ }
  }

  return <section className="settings-section"><div className="settings-description"><h2>Profile name</h2>
    <p>Your profile name is part of your app URL.</p>
    <p>If you change the name, the URL changes. The old URL stops working.</p>
  </div><div>
    <ErrorNotice error={state.accountProfile.error}/>
    {!profile && (state.accountProfile.status === 'idle' || state.accountProfile.status === 'loading') && <Loading/>}
    {profile && <form className="settings-key-form" onSubmit={event => void saveUsername(event)}>
      <label className="field"><span>Profile name</span>
        <input type="text" name="username" value={draft?.username ?? profile.username}
          onChange={event => setDraft({username: event.target.value, expectedUsername: draft?.expectedUsername ?? profile.username})}
          autoComplete="off" autoCapitalize="words" autoCorrect="off" spellCheck={false}
          required disabled={busy}/></label>
      {stale && <p className="field-help" role="status">Your saved name has changed. Select Use current name before you make another change.</p>}
      <div className="button-row"><button type="submit" className="button" disabled={busy || stale || !changed}>Save name</button>
        {draft !== null && <button type="button" className="text-button" disabled={busy}
          onClick={() => setDraft(null)}>Use current name</button>}</div>
      <p className="field-help">App URL: <span style={{overflowWrap: 'anywhere'}}>{profile.applicationBaseUrl}</span></p>
    </form>}
  </div></section>;
}
